/**
 * Performs case-insensitive substring matching across specified fields.
 * Returns all assets if query is empty (fewer than 1 character).
 */
export function textSearch<T>(assets: T[], query: string, searchableFields: (keyof T)[]): T[] {
  if (!query || query.length < 1) {
    return assets;
  }

  const lowerQuery = query.toLowerCase();

  return assets.filter((asset) =>
    searchableFields.some((field) => {
      const value = asset[field];
      if (value === null || value === undefined) {
        return false;
      }
      return String(value).toLowerCase().includes(lowerQuery);
    }),
  );
}

/**
 * Filters assets by site value.
 * Returns all assets if site is null, empty string, or "all".
 */
export function filterBySite<T extends { site: string }>(assets: T[], site: string | null): T[] {
  if (!site || site === '' || site.toLowerCase() === 'all') {
    return assets;
  }

  return assets.filter((asset) => asset.site === site);
}

/**
 * Filters assets by status value.
 * Returns all assets if status is null, empty string, or "all".
 */
export function filterByStatus<T extends { status?: string }>(
  assets: T[],
  status: string | null,
): T[] {
  if (!status || status === '' || status.toLowerCase() === 'all') {
    return assets;
  }

  return assets.filter((asset) => asset.status === status);
}

/**
 * Filter configuration for applyAllFilters.
 */
export interface FilterConfig<T> {
  textQuery: string;
  searchableFields: (keyof T)[];
  site: string | null;
  status: string | null;
}

/**
 * Applies all filters with AND logic.
 * Each filter narrows the result set; an asset must pass all active filters.
 */
export function applyAllFilters<T extends { site: string; status?: string }>(
  assets: T[],
  filters: FilterConfig<T>,
): T[] {
  let result = assets;

  result = textSearch(result, filters.textQuery, filters.searchableFields);
  result = filterBySite(result, filters.site);
  result = filterByStatus(result, filters.status);

  return result;
}

/**
 * Returns a sorted array of unique site values from the given assets.
 */
export function getDistinctSites<T extends { site: string }>(assets: T[]): string[] {
  const sites = new Set<string>();

  for (const asset of assets) {
    if (asset.site) {
      sites.add(asset.site);
    }
  }

  return Array.from(sites).sort();
}
